import { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import {
  useResource, PageTitle, Panel, Loading, ErrorState, EmptyState, Pill, DemoBadge,
  STATUS_TONE, fmtDate,
} from "../../components/app/Kit";

export default function Tasks() {
  const { data, state, error, reload } = useResource("/tasks");
  const [filter, setFilter] = useState("open");
  const [title, setTitle] = useState("");

  const rows = useMemo(() => {
    const list = data || [];
    if (filter === "all") return list;
    return list.filter((t) => t.status === filter);
  }, [data, filter]);

  const toggle = async (task) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status: task.status === "done" ? "open" : "done" });
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3) {
      toast.error("Give the task a title.");
      return;
    }
    try {
      await api.post("/tasks", {
        title: title.trim(), status: "open", priority: "medium",
        assigned_to: "Unassigned", due_at: new Date(Date.now() + 86400000).toISOString(),
      });
      setTitle("");
      toast.success("Task created.");
      reload();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="tasks-page">
      <PageTitle testId="tasks-title" title="Tasks" sub="Follow-ups and internal actions, whether created by a person or an automation." action={<DemoBadge />} />

      <Panel testId="tasks-create" title="Add task" className="mb-4">
        <form onSubmit={create} className="flex flex-wrap gap-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Call back about Thursday slot"
            data-testid="task-input-title"
            className="flex-1 rounded-md border border-elv-line bg-white/[0.02] px-3 py-2 text-xs text-elv-text outline-none focus:border-elv-green/40"
          />
          <button type="submit" data-testid="task-create-submit" className="rounded-md bg-elv-green px-4 py-2 text-xs font-semibold text-[#04120A]">
            Create task
          </button>
        </form>
      </Panel>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {["open", "done", "all"].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            data-testid={`tasks-filter-${f}`}
            className={`rounded-md border px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] ${
              filter === f ? "border-elv-green/35 bg-elv-green/[0.08] text-elv-green" : "border-elv-line bg-white/[0.02] text-elv-dim"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {state === "loading" ? <Loading rows={6} testId="tasks-loading" /> : null}
      {state === "error" ? <ErrorState message={error} onRetry={reload} testId="tasks-error" /> : null}
      {state === "ready" && rows.length === 0 ? (
        <EmptyState icon="ListChecks" title="No tasks here" sub="Tasks created by your team or by automations will appear in this list." testId="tasks-empty" />
      ) : null}

      {state === "ready" && rows.length > 0 ? (
        <div className="grid gap-2">
          {rows.map((t) => (
            <div key={t.id} data-testid={`task-${t.id}`} className="glass flex flex-wrap items-center justify-between gap-3 rounded-lg p-4">
              <button
                type="button"
                onClick={() => toggle(t)}
                data-testid={`task-toggle-${t.id}`}
                className="flex items-center gap-3 text-left"
              >
                {t.status === "done" ? (
                  <Icons.CheckCircle2 className="h-4 w-4 shrink-0 text-elv-green" />
                ) : (
                  <Icons.Circle className="h-4 w-4 shrink-0 text-elv-dim" />
                )}
                <span className={`text-xs ${t.status === "done" ? "text-elv-dim line-through" : "text-elv-text"}`}>
                  {t.title}
                </span>
              </button>
              <div className="flex flex-wrap items-center gap-2">
                {t.customer_name ? <span className="text-[11px] text-elv-dim">{t.customer_name}</span> : null}
                <Pill tone={t.priority === "high" ? "red" : t.priority === "medium" ? "amber" : "dim"}>{t.priority}</Pill>
                <Pill tone={STATUS_TONE[t.status] || "dim"}>{t.status}</Pill>
                <span className="font-mono text-[10px] text-elv-dim">due {fmtDate(t.due_at)}</span>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
